import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
// import { CartLineModel } from "@insite/client-framework/Types/ApiModels"; // Custom Code
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import React, { useState } from "react";
import { css } from "styled-components";

/* Custom Code */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ga from "@store/Framework/Common/GoogleAnalytics";
import useExpandProps from "@store/Framework/Common/Hooks/useExpandProps";
import { useSettingsCollection } from "@store/Framework/Store/Context/ContextHooks";
import { focusStyles } from "@store/Themes/common/mixins/common";
import { imageStyles } from "@store/Themes/common/mixins/image";
import upperFirst from "lodash/upperFirst";

type ProductImageSize = "small" | "medium" | "large";
/* Custom Code */

interface OwnProps {
    product: CartLineModel | ProductContextModel | ProductModel; // Custom Code
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
    // Custom Code
    overridedStyles?: ProductImageStyles;
    size?: ProductImageSize;
    href?: string;
}

type Props = OwnProps;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // Custom Code
    wrapper?: InjectableCss;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            ${focusStyles()}
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;

            > span {
                cursor: default;
            }
        `,
    },
};

const ProductImage = ({
    product: baseProduct, // Custom Code
    onClick,
    extendedStyles,
    showSmallImage,
    // Custom Code
    overridedStyles,
    size = "small",
    href,
}: Props) => {
    /* Custom Code */
    const styles = useExpandProps(productImageStyles, extendedStyles, overridedStyles);
    const product = "product" in baseProduct ? baseProduct.product : baseProduct;
    const trackSelectItem = ga.use.trackSelectItem({ items: product });
    const itemCategoryData = ga.use.itemCategoryData(); // Custom Code
    const { extensionCatalogSettings } = useSettingsCollection();

    let altText =
        ("imageAltText" in product && product.imageAltText) || ("altText" in product && product.altText) || "";
    if (!altText || altText === "") {
        altText =
            ("productTitle" in product && product.productTitle) ||
            ("productName" in product && product.productName) ||
            "";
    }

    const notFoundImagePath = (extensionCatalogSettings as any)?.[`notFound${upperFirst(size)}ImagePath`];
    const pathProperty = `${size}ImagePath`;
    const imagePath = (product as any).product?.[pathProperty] || (product as any)?.[pathProperty] || notFoundImagePath;

    const productDetailPath =
        "productInfo" in baseProduct
            ? baseProduct.productInfo.productDetailPath
            : ("productUri" in product && product.productUri) || "";

    const isActive = "isActive" in product ? product.isActive : true;

    const productIdx = "idx" in product && product.idx;
    const loading = productIdx !== undefined && parseInt(productIdx.toString(), 10) < 3 ? "eager" : "lazy";

    const clickableCss = css`
        ${x => imageStyles(x.theme.design.product.image[size].ratio)}
        ${styles.linkWrappingImage?.css}
    `;

    const clickableHref = href || (isActive && productDetailPath ? productDetailPath : undefined);

    return (
        <StyledWrapper {...styles.wrapper} onClick={() => trackSelectItem({}, { itemData: itemCategoryData })}>
            <Clickable
                {...styles.linkWrappingImage}
                href={clickableHref}
                onClick={onClick}
                data-test-selector="productImage"
                css={clickableCss}
            >
                {/* Removed - next line - width, height */}
                <Img {...styles.img} loading={loading} src={imagePath} altText={altText} />
            </Clickable>
        </StyledWrapper>
    );
    /* Custom Code */
};

export default ProductImage;
